import { useLoaderData } from 'react-router';
import { Button } from '@rocco/components/button';
import { Image } from '@rocco/components/image';
import { ExternalLinkIcon } from 'lucide-react';
import { useWebsiteReferralTracking } from '~/modules/tracking/util/websiteTracking';
import type { loader } from '~/routes/_app.project.$urlSegment._index';

/**
 * Dark banner CTA rendered below the project gallery and "Views and
 * Engagement" section. Targets highly qualified users who have scrolled
 * through all project photos.
 *
 * Responsive: stacks vertically on mobile, horizontal on desktop.
 *
 * Uses the same `useWebsiteReferralTracking` hook and `data-track-referral`
 * attribute pattern as every other "Visit Website" button so that clicks
 * fire the standard `Website` tracking event picked up by the global
 * mousedown listener in useLogLinkClicks.
 */
export const ProjectDesktopWebsiteCTA = () => {
    const { project } = useLoaderData<typeof loader>();

    const { referralTrackingURL, referralTrackingParams } =
        useWebsiteReferralTracking(
            project.WebsiteUntracked || project.Professional.Website,
            {
                libraryItemID: project.ID,
                professionalID: project.Professional.ID,
                professionalTitle: project.Professional.Title,
                source: 'ProjectWebsiteCTA',
            },
        );

    if (!referralTrackingURL) {
        return null;
    }

    const bannerUrl = project.Banner?.imageUrl;
    const logoUrl = project.Professional.CompanyLogo;
    const logoBg = project.Professional.LogoBackgroundColor || 'white';

    return (
        <div className="mx-auto w-full max-w-[113.75rem] px-4 pt-6 pb-10 md:px-0 md:pt-8 md:pb-32">
            <div className="relative overflow-hidden rounded-2xl bg-[#141414]">
                {bannerUrl && (
                    <img
                        src={bannerUrl}
                        alt=""
                        className="absolute inset-0 h-full w-full object-cover opacity-15"
                    />
                )}

                <div className="relative flex flex-col gap-6 px-6 py-8 md:flex-row md:items-center md:justify-between md:px-16 md:py-12">
                    <div className="flex items-center gap-5 md:gap-8">
                        {logoUrl && (
                            <div
                                className="flex h-14 w-14 shrink-0 items-center justify-center overflow-hidden rounded-full md:h-20 md:w-20"
                                style={{ backgroundColor: logoBg }}
                            >
                                <Image
                                    src={logoUrl}
                                    alt={project.Professional.Title}
                                    width={60}
                                    height={60}
                                    className="h-10 w-10 object-contain md:h-15 md:w-15"
                                />
                            </div>
                        )}
                        <div className="flex flex-col gap-1 md:gap-2">
                            <span className="font-display text-sm font-medium tracking-wide text-white/60 md:text-base">
                                Inspired by this project?
                            </span>
                            <span className="font-display text-xl font-semibold text-white md:text-3xl">
                                Visit {project.Professional.Title}
                            </span>
                        </div>
                    </div>
                    <Button
                        color="outline"
                        inverted
                        className="w-full px-8 py-3.5 text-base transition-all duration-300 hover:bg-white hover:fill-[#141414] hover:text-[#141414] md:w-auto md:px-12 md:py-4 md:text-lg"
                        asChild
                    >
                        <a
                            href={referralTrackingURL}
                            target="_blank"
                            {...referralTrackingParams}
                            rel="noopener"
                        >
                            VISIT WEBSITE
                            <ExternalLinkIcon className="ml-2 h-4 w-4" />
                        </a>
                    </Button>
                </div>
            </div>
        </div>
    );
};
